import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Eye, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

const scoreColor = (s) => s >= 75 ? "#2A9D8F" : s >= 55 ? "#E9C46A" : "#E06A3B";

export default function FamilyView() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/family/view/${userId}`).then(r => setData(r.data)).catch(() => setData({ error: true })); }, [userId]);

  if (!data) return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--gold)]" /></div>;
  if (data.error) return <div className="p-8 text-center text-[color:var(--terracotta)]">Access denied. This user hasn't shared with you.</div>;

  const { owner, profile, health_score } = data;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/family" className="text-sm text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Family
        </Link>
        <div className="text-xs px-3 py-1 rounded-full bg-[rgba(42,157,143,0.15)] text-[color:var(--emerald)] inline-flex items-center gap-1 uppercase tracking-widest">
          <Eye className="w-3 h-3" /> Read-only view
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="w-16 h-16 rounded-full bg-[rgba(212,175,55,0.15)] flex items-center justify-center text-[color:var(--gold)] text-2xl font-semibold">
          {owner.name?.[0]?.toUpperCase()}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Viewing</div>
          <h1 className="font-heading text-3xl font-bold">{owner.name}'s money</h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8">
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Financial Health</div>
          <div className="font-mono text-6xl font-bold mb-6" style={{ color: scoreColor(health_score.overall) }}>
            {health_score.overall}<span className="text-2xl text-[color:var(--text-muted)]">/100</span>
          </div>
          <div className="space-y-3">
            {health_score.breakdown.map((s) => (
              <div key={s.name} className="flex items-center gap-4">
                <div className="w-36 text-sm text-[color:var(--text-secondary)]">{s.name}</div>
                <div className="flex-1 h-2 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${s.score}%`, background: scoreColor(s.score) }} />
                </div>
                <div className="font-mono text-sm font-semibold w-10 text-right">{s.score}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 gap-4">
          {[
            { label: "Cash", value: profile.cash, color: "#F5EBE6" },
            { label: "Investments", value: profile.investments, color: "#2A9D8F" },
            { label: "Debt", value: profile.debt, color: "#E06A3B" },
            { label: "Net Worth", value: profile.net_worth, color: "#D4AF37" },
          ].map(s => (
            <div key={s.label} className="glass-card p-5">
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">{s.label}</div>
              <div className="font-mono text-xl font-bold" style={{ color: s.color }}>${Math.abs(s.value).toLocaleString()}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="glass-card p-6">
        <h3 className="font-heading text-lg font-semibold mb-4">Goals</h3>
        <div className="space-y-4">
          {profile.goals.map(g => {
            const pct = Math.min(100, (g.current / g.target) * 100);
            return (
              <div key={g.goal_id}>
                <div className="flex justify-between mb-1 text-sm">
                  <span className="font-medium">{g.title}</span>
                  <span className="font-mono text-[color:var(--text-secondary)]">${g.current.toLocaleString()} / ${g.target.toLocaleString()}</span>
                </div>
                <div className="h-1.5 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#E9C46A]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
